import React, { useEffect, useMemo } from 'react';
import { Alert } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import {
  PRODUCTION_ASSEMBLY_TAB_ROUTES,
  getProductionAssemblyPathForTab,
  getProductionAssemblyTabFromPath,
} from '../../config/productionRoutes';
import TopNav from '../TopNav/TopNav';
import AssemblyAssemblerManager from './AssemblyAssemblerManager';
import AssemblySchedule from './AssemblySchedule';
import AssemblyScheduleChangeLog from './AssemblyScheduleChangeLog';

const AssemblyContainer = () => {
  const { canView, canEdit, isLoading: isPermissionsLoading } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const visibleTabs = useMemo(
    () => PRODUCTION_ASSEMBLY_TAB_ROUTES.filter((tab) => canView(tab.permissionKey)),
    [canView]
  );
  const requestedTab = getProductionAssemblyTabFromPath(location.pathname);
  const activeTab = requestedTab && visibleTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : visibleTabs[0]?.id || '';

  useEffect(() => {
    if (isPermissionsLoading || visibleTabs.length === 0 || !activeTab) return;

    const canonicalPath = getProductionAssemblyPathForTab(activeTab);
    const currentPath = location.pathname.replace(/\/+$/, '').toLowerCase();
    if (canonicalPath && currentPath !== canonicalPath.toLowerCase()) {
      navigate(canonicalPath, { replace: true });
    }
  }, [
    activeTab,
    isPermissionsLoading,
    location.pathname,
    navigate,
    visibleTabs.length,
  ]);

  if (isPermissionsLoading) {
    return (
      <div>
        <TopNav />
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading assembly permissions...</p>
        </div>
      </div>
    );
  }

  if (!canView('assembly.container')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <TopNav />
      <main className="container-fluid p-3">
        {visibleTabs.length === 0 ? (
          <Alert variant="warning" className="mb-0">
            You do not currently have access to any assembly tabs. Contact an administrator to update your component policies.
          </Alert>
        ) : (
          <>
            <ul className="nav nav-tabs mb-4">
              {visibleTabs.map((tab) => (
                <li className="nav-item" key={tab.id}>
                  <button
                    type="button"
                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => navigate(getProductionAssemblyPathForTab(tab.id))}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
            <div className="tab-content">
              {activeTab === 'assemblySchedule' && (
                <AssemblySchedule canEdit={canEdit('assembly.schedule')} />
              )}
              {activeTab === 'assemblyAssemblers' && (
                <AssemblyAssemblerManager canEdit={canEdit('assembly.assemblers')} />
              )}
              {activeTab === 'assemblyChangeLog' && <AssemblyScheduleChangeLog />}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AssemblyContainer;
